<template>
  <v-card class="main-card">
    <v-card-title class="screen-title">
      <span>同意履歴一覧</span>
      <manual-button path="/pre-agreement/" />
    </v-card-title>
    <v-card-text class="pa-2" v-if="isLoaded">
      <v-row no-gutters>
        <v-col>
          <app-search
            :search="agreement.search"
            v-model="agreement.filter"
            :initial-form="
              () => ({
                postStartDate: { start: null, end: null },
                signedDate: { start: null, end: null },
              })
            "
          >
            <template #input(custom)="{ form, option }">
              <v-row class="align-center justify-center">
                <template v-if="option.key === 'postStartDate'">
                  <!--掲載開始日時の期間検索-->
                  <v-col>
                    <validation-provider
                      name="postStartDateStart"
                      label="開始"
                      v-model="form['postStartDate']['start']"
                      :rules="{
                        required: true,
                        date_format: 'YYYY-MM-DDTHH:mm',
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        :hide-details="true"
                        type="datetime-local"
                        v-bind="componentField"
                        :error-messages="errorMessage"
                        :field-meta="meta"
                      ></app-form-input>
                    </validation-provider>
                  </v-col>
                  <span class="mx-2">〜</span>
                  <v-col>
                    <validation-provider
                      name="postStartDateEnd"
                      label="終了"
                      v-model="form['postStartDate']['end']"
                      :rules="{
                        required: true,
                        date_format: 'YYYY-MM-DDTHH:mm',
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        :hide-details="true"
                        type="datetime-local"
                        v-bind="componentField"
                        :error-messages="errorMessage"
                        :field-meta="meta"
                      ></app-form-input>
                    </validation-provider>
                  </v-col>
                </template>
                <template v-else>
                  <!--同意日時の期間検索-->
                  <v-col>
                    <validation-provider
                      name="signedDateStart"
                      label="開始"
                      v-model="form['signedDate']['start']"
                      :rules="{
                        required: true,
                        date_format: 'YYYY-MM-DDTHH:mm',
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        :hide-details="true"
                        type="datetime-local"
                        v-bind="componentField"
                        :error-messages="errorMessage"
                        :field-meta="meta"
                      ></app-form-input>
                    </validation-provider>
                  </v-col>
                  <span class="mx-2">〜</span>
                  <v-col>
                    <validation-provider
                      name="signedDateEnd"
                      label="終了"
                      v-model="form['signedDate']['end']"
                      :rules="{
                        required: true,
                        date_format: 'YYYY-MM-DDTHH:mm',
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        :hide-details="true"
                        type="datetime-local"
                        v-bind="componentField"
                        :error-messages="errorMessage"
                        :field-meta="meta"
                      ></app-form-input>
                    </validation-provider>
                  </v-col>
                </template>
                <v-btn icon="$mdiMagnify" type="submit"></v-btn>
              </v-row>
            </template>

            <!--掲載開始日時のタグ表示-->
            <template #tagValue(postStartDate)="{ value }">
              {{ $filter.datetime(value.start) }} ~
              {{ $filter.datetime(value.end) }}
            </template>

            <!--同意日時のタグ表示-->
            <template #tagValue(signedDate)="{ value }">
              {{ $filter.datetime(value.start) }} ~
              {{ $filter.datetime(value.end) }}
            </template>
          </app-search>
        </v-col>
      </v-row>
      <div class="d-flex flex-row justify-end mb-2">
        <v-btn color="primary" @click="load">一覧更新</v-btn>
      </div>
      <app-table
        v-model:table-sort-by="agreement.sortBy"
        :fields="agreement.fields"
        :items="agreement.items"
        primary-key="agreementSeq"
        :filter="agreement.filter"
        :filter-option="agreement.filter_option"
        :hover="true"
        @click:row="historyDetails"
        sticky-header="50vh"
        :td-class="cellClass"
      >
        <template #cell(postStartDate)="{ value }">
          {{ $filter.datetime(value) }}
        </template>
        <template #cell(signedDate)="{ value }">
          {{ $filter.datetime(value, { s: true }) }}
        </template>
        <template #cell(agreementStatus)="{ value }">
          {{ $filter.enumTo(value, "agreementStatusItems", "未同意") }}
        </template>
      </app-table>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import AgreementReference from "@/modals/agreement/AgreementReference.vue";
import type { AgreementInfo, AgreementInfoList } from "@/apis/AgreementApi";
import type {
  AppTableHeader,
  FilterOption,
  SortItem,
} from "@/components/AppTable.vue";
import type { SearchOption } from "@/components/AppSearch.vue";

export default defineComponent({
  name: "AgreementHistory",
  data() {
    return {
      isLoaded: false,
      agreements: {} as AgreementInfo,
      agreement: {
        sortBy: {
          key: "postStartDate",
          order: "desc",
        } as SortItem,
        fields: [
          {
            key: "postStartDate",
            title: "掲載開始日時",
            sortable: true,
            width: "200px",
          },
          {
            key: "agreementTitle",
            title: "タイトル",
            sortable: true,
          },
          {
            key: "signedDate",
            title: "同意日時",
            sortable: true,
            width: "200px",
            sortByFormatted: true,
            filterByFormatted: true,
            formatter: (value: string, _k: string, item: AgreementInfoList) => {
              if (item.agreementStatus === "ON_HOLD") {
                return null;
              } else {
                return item.signedDate;
              }
            },
          },
          {
            key: "agreementStatus",
            title: "状態",
            sortable: true,
            width: "100px",
          },
        ] as AppTableHeader<AgreementInfoList>[],
        items: [] as AgreementInfoList[],
        search: {
          postStartDate: { type: "custom", label: "掲載開始日時" },
          agreementTitle: { type: "text", label: "タイトル" },
          signedDate: { type: "custom", label: "同意日時" },
          agreementStatus: {
            type: "select",
            label: "状態",
            items: [
              { value: null, text: "未同意" },
              { value: "AGREED", text: "同意済み" },
              { value: "ON_HOLD", text: "同意保留" },
            ],
          },
        } as SearchOption,
        filter: {} as Record<string, unknown>,
        filter_option: {
          postStartDate: (
            item: AgreementInfoList,
            value: { start: string; end: string }
          ) => {
            return this.$moment(item.postStartDate).isBetween(
              this.$moment(value.start),
              this.$moment(value.end),
              undefined,
              "[]"
            );
          },
          signedDate: (
            item: AgreementInfoList,
            value: { start: string; end: string }
          ) => {
            return this.$moment(
              item.agreementStatus === "ON_HOLD" ? null : item.signedDate
            ).isBetween(
              this.$moment(value.start),
              this.$moment(value.end),
              undefined,
              "[]"
            );
          },
          agreementStatus: "equal",
          agreementTitle: "like",
        } as FilterOption,
      },
    };
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  methods: {
    async load() {
      this.agreements = await this.$api.agreement.getAll();
      this.agreement.items = this.agreements.agreementList;
    },
    /** 同意詳細画面を表示*/
    async historyDetails(item: AgreementInfoList) {
      await this.$modal.show(AgreementReference, {
        agreementItem: item,
        agreementKeyId: this.agreements.agreementKeyId,
        // 最新のレコードか否かを設定
        isLatest:
          this.agreement.items.sortBy(["postStartDate", "desc"])[0]
            .agreementSeq === item.agreementSeq,
      });
      await this.load();
    },
    /** tdにクラスをつける */
    cellClass(field: AppTableHeader<unknown>): string[] | undefined {
      if (field.key === "agreementTitle") {
        return ["text-pre-wrap"];
      }
      return undefined;
    },
  },
});
</script>
